import type {
  SessionDTO,
  Session,
  SessionRecap,
  SessionRecapDTO,
  SessionStatusResponse,
  SessionStatusResponseDTO,
  MusicSources,
  MusicSourcesDTO,
} from "@akina/domain";
import { toCamelCase } from "@akina/utils";
import { nullableDecodeHtmlTextFromString } from "@akina/utils/decodeHtmlTextFromString";
import type {
  MaiaEchoCredentialsDTO,
  MaiaEchoCredentialsDTOApi,
  ReactionEnum,
  SessionOverview,
  TreatmentFeedbackResponse,
  TreatmentFeedbackResponseApi,
} from "types/schemas";
import { musicSourcesMapper, sessionMapper, sessionRecapMapper, sessionStatusResponseMapper } from "../mappers";
import { type ApiConfiguration, ApiMethod } from "./shared";

export const therapySessionApi = (config: ApiConfiguration) => ({
  async getSessionFeedback(treatmentId: string, reviewedOnly?: boolean): Promise<SessionOverview[]> {
    const result = await config.httpService.fetch<TreatmentFeedbackResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.feedback.get(treatmentId, reviewedOnly),
    });

    const response = toCamelCase<TreatmentFeedbackResponseApi, TreatmentFeedbackResponse>(result);

    const feedback: SessionOverview[] = (response.treatmentFeedback ?? []).map((session): SessionOverview => {
      const therapistFeedback = session.therapistFeedback
        ? {
            ...session.therapistFeedback,
            text: nullableDecodeHtmlTextFromString(session.therapistFeedback.text ?? undefined) ?? null,
          }
        : null;

      return {
        ...session,
        selfReflectionComment: nullableDecodeHtmlTextFromString(session.selfReflectionComment ?? undefined) ?? null,
        therapistFeedback,
        routineOverviews: session.routineOverviews.map(routine => ({
          ...routine,
          therapistFeedback: routine.therapistFeedback
            ? {
                ...routine.therapistFeedback,
                text: nullableDecodeHtmlTextFromString(routine.therapistFeedback.text ?? undefined) ?? null,
              }
            : null,
        })),
      };
    });

    return feedback.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  },
  async setFeedbackViewed(
    treatmentId: string,
    sessionId: string,
  ): Promise<{
    feedback: SessionOverview[];
    treatmentId: string;
    sessionId: string;
  }> {
    await config.httpService.fetch<void>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.feedback.viewed,
      data: {
        treatment_id: treatmentId,
        session_log_id: sessionId,
      },
    });

    return {
      feedback: await this.getSessionFeedback(treatmentId),
      treatmentId,
      sessionId,
    };
  },
  async getSessionRecap(sessionId: string): Promise<SessionRecap | undefined> {
    const result = await config.httpService.fetch<SessionRecapDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.sessionRecap(sessionId),
    });

    return sessionRecapMapper.toDomain(result);
  },
  async getSessionStatus(sessionId: string): Promise<SessionStatusResponse | undefined> {
    const result = await config.httpService.fetch<SessionStatusResponseDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.sessionStatus(sessionId),
    });
    return sessionStatusResponseMapper.toDomain(result);
  },
  async getMaiaEchoCredentials(
    sessionId: string,
    routineId: number,
    signal?: AbortSignal,
  ): Promise<MaiaEchoCredentialsDTO> {
    const result = await config.httpService.fetch<MaiaEchoCredentialsDTOApi>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.maiaEchoCredentials(sessionId, routineId),
      signal,
    });

    return toCamelCase(result);
  },
  async getMusicSources(stationId?: string): Promise<MusicSources> {
    const response = await config.httpService.fetch<MusicSourcesDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.musicSources(stationId ?? ""),
    });

    return musicSourcesMapper.toDomain(response);
  },
  async sendSessionFeedbackReaction(sessionId: string, reaction: ReactionEnum | null): Promise<void> {
    await config.httpService.fetch<void>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.feedback.react,
      data: {
        session_log_id: sessionId,
        reaction,
      },
    });
  },
  async sendRoutineFeedbackReaction(
    sessionId: string,
    routineId: number,
    reaction: ReactionEnum | null,
  ): Promise<void> {
    await config.httpService.fetch<void>({
      method: ApiMethod.PATCH,
      url: config.endpoints.sessionReview.routineFeedback.react,
      data: {
        session_log_id: sessionId,
        routine_log_id: routineId,
        reaction,
      },
    });
  },
  async getNextSession(
    treatmentId: string,
    params: { includeMsps: boolean; onlyScriptGeneration: boolean },
  ): Promise<Session | undefined> {
    const result = await config.httpService.fetch<SessionDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.therapySessions.nextSession(treatmentId, params),
    });

    return sessionMapper.toDomain(result);
  },
  async startSession(sessionId: string): Promise<void> {
    await config.httpService.fetch<string>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.startSession,
      data: { session_log_id: sessionId },
    });

    return;
  },
  async endSession(sessionId: string): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.endSession,
      data: { session_log_id: sessionId },
    });
  },
  async cancelSession(sessionId: string): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.cancelSession,
      data: { session_log_id: sessionId },
    });
  },
  async cancelRoutine({
    routineId,
    sessionId,
    totalCompletedSetBlocks,
    exerciseTemplateKey,
    routineMetric,
  }: {
    routineId: number;
    sessionId: string;
    totalCompletedSetBlocks: number;
    exerciseTemplateKey: string;
    routineMetric: string;
  }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.cancelRoutine,
      data: {
        routine_log_id: routineId,
        session_log_id: sessionId,
        number_of_set_blocks_completed: totalCompletedSetBlocks,
        exercise_template_key: exerciseTemplateKey,
        routine_metric: routineMetric,
      },
    });
  },
  async updateRoutineProgress({
    routineId,
    sessionId,
    routineMetric,
    totalCompletedSetBlocks,
    exerciseTemplateKey,
    signal,
  }: {
    routineId: number;
    sessionId: string;
    routineMetric: string;
    totalCompletedSetBlocks: number;
    exerciseTemplateKey: string;
    signal?: AbortSignal;
  }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.updateRoutineProgress,
      data: {
        routine_log_id: routineId,
        session_log_id: sessionId,
        routine_metric: routineMetric,
        number_of_set_blocks_completed: totalCompletedSetBlocks,
        exercise_template_key: exerciseTemplateKey,
      },
      signal,
    });
  },
  async updateSelfReflection({
    sessionId,
    painLevel,
    painfulExerciseIds,
    difficultExerciseIds,
    feedback,
    wasDifficult,
  }: {
    sessionId: string;
    painLevel: number;
    painfulExerciseIds: string[];
    difficultExerciseIds: string[];
    feedback: string | undefined;
    wasDifficult: boolean;
  }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapySessions.updateSelfReflection,
      data: {
        session_log_id: sessionId,
        pain_level: painLevel,
        painful_exercises: painfulExerciseIds,
        difficult_exercises: difficultExerciseIds,
        comments: feedback,
        was_difficult: wasDifficult,
      },
    });
  },
});
